import { Link, useParams } from 'react-router-dom';
import type { Article } from '@/types/content';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import usePageMeta from '@/hooks/usePageMeta';
import JsonLd from '@/components/feature/JsonLd';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/base/ArticleCard';
import HeatBadge from '@/components/base/HeatBadge';
import Timeline from '@/pages/article/components/Timeline';

const allArticles = articles as Article[];

const ArticlePage = () => {
  const { id } = useParams();
  const article = allArticles.find((item) => item.id === id);

  usePageMeta({
    title: article ? `${article.title} - 爆瓜爆料第一现场` : '内容不存在 - 爆瓜爆料',
    description: article
      ? `${article.excerpt.slice(0, 70)}……爆瓜爆料第一时间追踪该事件进展，附第一现场时间线。`
      : '该爆料内容暂时不存在，可返回爆瓜爆料首页查看最新快讯。',
    keywords: article ? article.tags.join(',') : '爆瓜爆料,娱乐快讯',
    canonical: article ? `https://sibzb.cn/article/${article.id}` : 'https://sibzb.cn/',
  });

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex flex-col items-center justify-center text-center px-4 py-24">
          <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center mb-5">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <h1 className="font-heading text-2xl font-black text-foreground-950">这条瓜好像已经下线了</h1>
          <p className="mt-3 text-sm text-foreground-600">可能是链接有误，或是内容正在重新核实。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className="ri-arrow-left-line" />
            返回首页看热榜
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = allArticles
    .filter((item) => item.categorySlug === article.categorySlug && item.id !== article.id)
    .slice(0, 3);
  const moreLikeThis = related.length > 0 ? related : allArticles.filter((item) => item.id !== article.id).slice(0, 3);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              image: article.cover,
              datePublished: article.publishedAt,
              dateModified: article.publishedAt,
              articleSection: article.category,
              keywords: article.tags.join(','),
              author: { '@type': 'Organization', name: article.author },
              publisher: { '@type': 'Organization', name: siteInfo.brand },
              mainEntityOfPage: `https://sibzb.cn/article/${article.id}`,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sibzb.cn/' },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: article.category,
                  item: `https://sibzb.cn/category/${article.categorySlug}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                  item: `https://sibzb.cn/article/${article.id}`,
                },
              ],
            },
          ],
        }}
      />
      <SiteHeader />

      <main className="flex-1 px-4 md:px-6 py-6 md:py-8">
        <Breadcrumbs
          items={[
            { label: '首页', to: '/' },
            { label: article.category, to: `/category/${article.categorySlug}` },
            { label: '正文' },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <article className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Link
                to={`/category/${article.categorySlug}`}
                className="rounded-full bg-primary-100 text-primary-800 px-2.5 py-1 text-xs transition-colors hover:bg-primary-200"
              >
                {article.category}
              </Link>
              <HeatBadge heat={article.heat} />
              <span className="text-xs text-foreground-500">吃瓜热度持续上升中</span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-black leading-snug text-foreground-950">
              {article.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 pb-4 border-b border-background-200">
              <span className="flex items-center gap-1">
                <i className="ri-user-3-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {article.publishedAt}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-timer-line" />
                阅读约 {article.readTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-3-line" />
                {article.comments} 条讨论
              </span>
            </div>

            <figure className="mt-5">
              <div className="w-full h-56 md:h-80 overflow-hidden rounded-lg bg-background-200">
                <img
                  src={article.cover}
                  alt={`${article.category}爆料现场配图：${article.title}`}
                  title={`${article.title} - 爆瓜爆料第一现场`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <figcaption className="mt-2 text-xs text-foreground-500">
                图源：爆瓜爆料编辑整理 · 用于事件说明
              </figcaption>
            </figure>

            <div className="mt-6 space-y-5">
              {article.blocks.map((block, index) => {
                if (block.type === 'h2') {
                  return (
                    <h2
                      key={`block-${index}`}
                      className="font-heading text-xl font-bold text-foreground-950 pt-2"
                    >
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === 'quote') {
                  return (
                    <blockquote
                      key={`block-${index}`}
                      className="border-l-4 border-accent-400 bg-accent-50 rounded-r-lg px-4 py-3 text-sm leading-7 text-accent-900"
                    >
                      {block.text}
                    </blockquote>
                  );
                }
                return (
                  <p key={`block-${index}`} className="text-[15px] leading-8 text-foreground-800">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500 mr-1">相关标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 text-secondary-800 px-3 py-1 text-xs whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-foreground-700">
                <i className="ri-information-line text-primary-600 mr-1" />
                内容以核实为准，后续进展将在本页持续更新。
              </p>
              <Link
                to="/contact"
                className="inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-4 py-2 text-sm text-primary-800 transition-colors hover:bg-primary-100"
              >
                <i className="ri-send-plane-line" />
                补充线索
              </Link>
            </div>
          </article>

          <aside className="lg:col-span-1 flex flex-col gap-6">
            <Timeline items={article.timeline} />

            <section className="bg-background-100 border border-background-200 rounded-lg p-5" aria-labelledby="more-title">
              <h2 id="more-title" className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-links-line text-primary-600" />
                相关推荐
              </h2>
              <div className="flex flex-col gap-3">
                {moreLikeThis.map((item) => (
                  <ArticleCard key={item.id} article={item} variant="row" />
                ))}
              </div>
            </section>

            <section className="rounded-lg bg-primary-800 text-background-50 p-5">
              <h2 className="font-heading text-base font-bold flex items-center gap-2">
                <i className="ri-notification-3-line text-secondary-300" />
                不错过任何大瓜
              </h2>
              <p className="mt-2 text-sm leading-6 text-primary-100">
                收藏本页即可查看该事件的最新进展，我们会在原页面追加更新，不做标题党式重复发布。
              </p>
              <Link
                to="/faq"
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-400 px-4 py-2 text-sm text-secondary-950 transition-colors hover:bg-secondary-300"
              >
                了解更新机制
                <i className="ri-arrow-right-line" />
              </Link>
            </section>
          </aside>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default ArticlePage;